// What an upload actually did.
//
// A contact upload can succeed and import nothing. Measured against a real
// export from the Yobi-1 desktop app: 31 patients in, 0 contacts out, HTTP 200,
// no error shown. The practice would have launched a campaign that called
// nobody, with nothing on screen to say why.
//
// So the result of an upload is now shown as a count out of the rows supplied,
// with every dropped row listed and attributed. The rule this follows: a row
// the practice handed us is either imported or explained, never silently gone.
import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Collapse,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ImportReport, SkippedRow } from '../services/campaignApi';

interface Props {
  report: ImportReport;
}

const SkipTable: React.FC<{ rows: SkippedRow[]; reason: string; showPhone?: boolean }> = ({
  rows, reason, showPhone,
}) => {
  if (!rows.length) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" gutterBottom>
        {rows.length} {rows.length === 1 ? 'row' : 'rows'} {reason}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 70 }}>Row</TableCell>
            <TableCell>Name</TableCell>
            {showPhone && <TableCell>Number</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.slice(0, 10).map((r, i) => (
            <TableRow key={i}>
              {/* The row number is what makes this fixable: the practice can
                  open their file and go straight to it. */}
              <TableCell>{r.row}</TableCell>
              <TableCell>{r.name}</TableCell>
              {showPhone && (
                <TableCell>
                  {r.phone}
                  {r.same_as_row ? ` (same as row ${r.same_as_row})` : ''}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 10 && (
        <Typography variant="caption" color="text.secondary">
          and {rows.length - 10} more
        </Typography>
      )}
    </Box>
  );
};

const ImportResult: React.FC<Props> = ({ report }) => {
  const dropped =
    report.skipped_no_phone.length +
    report.skipped_unreachable.length +
    report.duplicates.length;
  const allIn = dropped === 0 && report.imported > 0;
  const noneIn = report.imported === 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mt: 2,
        borderColor: (t) =>
          noneIn ? t.palette.error.main : allIn ? t.palette.success.main : t.palette.warning.main,
        bgcolor: (t) =>
          alpha(
            noneIn ? t.palette.error.main : allIn ? t.palette.success.main : t.palette.warning.main,
            0.05,
          ),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography
          sx={{ fontSize: 34, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
          color={noneIn ? 'error.main' : allIn ? 'success.main' : 'warning.main'}
        >
          {report.imported}
        </Typography>
        <Typography variant="body1">
          of {report.total_rows} {report.total_rows === 1 ? 'row' : 'rows'} added to this list
        </Typography>
        {allIn && <CheckCircleIcon color="success" fontSize="small" />}
      </Box>

      {noneIn && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Nothing was imported</AlertTitle>
          Launching now would call nobody. Check the dropped rows below, fix the file, and upload
          it again.
        </Alert>
      )}

      {report.unrecognised_columns.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Kept as extra information the agent can use on the call:
          </Typography>
          {report.unrecognised_columns.map((c) => (
            <Chip key={c} label={c} size="small" sx={{ mr: 0.6, mb: 0.6 }} />
          ))}
        </Box>
      )}

      <Collapse in={dropped > 0}>
        <SkipTable rows={report.skipped_no_phone} reason="had no phone number" />
        <SkipTable rows={report.skipped_unreachable} reason="had a number that cannot be dialled" showPhone />
        <SkipTable
          rows={report.duplicates}
          reason="repeat a number already on the list, and will be called once"
          showPhone
        />
      </Collapse>
    </Paper>
  );
};

export default ImportResult;
